import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { inventoryService } from "../services/database";
import { useAuth } from "../contexts/AuthContext";

interface AddInventoryItemFormProps {
  onSuccess?: (itemId: string) => void;
  onCancel?: () => void;
}

export default function AddInventoryItemForm({
  onSuccess,
  onCancel,
}: AddInventoryItemFormProps) {
  const { user, currentOrganization } = useAuth();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    name: "",
    category: "",
    description: "",
    manufacturer: "",
    model: "",
    dry_hire_price: "",
    location: "",
    notes: "",
    weight: "",
    purchase_price: "",
  });

  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [error, setError] = useState("");

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (value === "" || !isNaN(Number(value))) {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleImageChange = (_e: React.ChangeEvent<HTMLInputElement>) => {
    // Image upload functionality removed - TODO: implement later
    console.warn("Image upload not implemented yet");
  };

  const removeImage = (index: number) => {
    // Revoke the object URL to free memory
    URL.revokeObjectURL(imageUrls[index]);
    setImageUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadImages = async (): Promise<string[]> => {
    // TODO: Add image upload logic later
    console.warn("Image upload not implemented yet");
    return [];
  };

  const addItemMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("User not authenticated");
      if (!currentOrganization) throw new Error("No organization selected");

      try {
        // Create the inventory item first without images
        const item = await inventoryService.addInventoryItem({
          name: formData.name,
          category: formData.category,
          images: [], // Start with empty images array
          description: formData.description || null,
          manufacturer: formData.manufacturer || null,
          model: formData.model || null,
          dry_hire_price: formData.dry_hire_price
            ? parseFloat(formData.dry_hire_price)
            : null,
          location: formData.location || null,
          notes: formData.notes || null,
          weight: formData.weight || null,
          purchase_price: formData.purchase_price
            ? parseFloat(formData.purchase_price)
            : null,
          created_by: user.id,
          updated_by: user.id,
          organization_id: currentOrganization.id,
        });

        // Then try to upload images
        try {
          const uploadedImageUrls = await uploadImages();

          // If we have uploaded images, update the item
          if (uploadedImageUrls.length > 0) {
            await inventoryService.updateInventoryItem(
              item.id,
              {
                images: uploadedImageUrls,
                updated_by: user.id,
              },
              currentOrganization.id
            );

            // Update the item with the image URLs
            item.images = uploadedImageUrls;
          }
        } catch (imageError) {
          console.error(
            "Failed to upload images, but item was created:",
            imageError
          );
          // We don't rethrow here, as we want to return the created item even if image upload fails
        }

        return item;
      } catch (error: any) {
        setError(error.message || "Failed to add inventory item");
        throw error;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["inventory", currentOrganization?.id],
      });
      if (onSuccess) onSuccess(data.id);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!formData.name) {
      setError("Name is required");
      return;
    }

    if (!formData.category) {
      setError("Category is required");
      return;
    }

    addItemMutation.mutate();
  };

  return (
    <div className="bg-white rounded-lg">
      <h2 className="text-xl font-semibold mb-4">Add New Inventory Item</h2>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
              required
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="category"
              value={formData.category}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
              required
            />
          </div>

          {/* Manufacturer */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Manufacturer
            </label>
            <input
              type="text"
              name="manufacturer"
              value={formData.manufacturer}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Model */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Model
            </label>
            <input
              type="text"
              name="model"
              value={formData.model}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Dry Hire Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Dry Hire Price
            </label>
            <input
              type="text"
              name="dry_hire_price"
              value={formData.dry_hire_price}
              onChange={handleNumberChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="0.00"
            />
          </div>

          {/* Purchase Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Purchase Price
            </label>
            <input
              type="text"
              name="purchase_price"
              value={formData.purchase_price}
              onChange={handleNumberChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="0.00"
            />
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Location
            </label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Weight */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Weight
            </label>
            <input
              type="text"
              name="weight"
              value={formData.weight}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="e.g. 5kg"
            />
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            maxLength={500}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            placeholder="Add any additional notes about this item..."
          />
          <div className="flex justify-between items-center mt-1">
            <span className="text-xs text-gray-500">
              {formData.notes.length}/500 characters
            </span>
            {formData.notes.length >= 450 && (
              <span className="text-xs text-orange-600">
                {formData.notes.length >= 500
                  ? "Character limit reached"
                  : "Approaching limit"}
              </span>
            )}
          </div>
        </div>

        {/* Image Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Images
          </label>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <p className="text-xs text-gray-500 mt-1">
            Note: Image upload requires proper storage permissions. If images
            fail to upload, the item will still be created.
          </p>

          {/* Image Previews */}
          {imageUrls.length > 0 && (
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {imageUrls.map((url, index) => (
                <div key={index} className="relative">
                  <img
                    src={url}
                    alt={`Preview ${index + 1}`}
                    className="h-24 w-24 object-cover rounded-md"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            disabled={addItemMutation.isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50"
            disabled={addItemMutation.isPending}
          >
            {addItemMutation.isPending ? "Saving..." : "Save Item"}
          </button>
        </div>
      </form>
    </div>
  );
}
